// utils/timeFormatter.ts

/**
 * Formatuje czas z formatu HH:MM:SS na H:MM
 * @param timeString - czas w formacie "06:00:00"
 * @returns sformatowany czas "6:00"
 */
export const formatTime = (timeString: string): string => {
  if (!timeString) return "";

  try {
    const parts = timeString.split(":");
    if (parts.length < 2) {
      console.warn(`Invalid time format: ${timeString}`);
      return timeString;
    }
    const [hours, minutes] = parts;
    const hour = parseInt(hours || "0", 10);
    return `${hour}:${minutes}`;
  } catch (error) {
    console.warn(`Invalid time format: ${timeString} ${error}`);
    return timeString;
  }
};

/**
 * Formatuje zakres godzin
 * @param startTime - czas rozpoczęcia "06:00:00"
 * @param endTime - czas zakończenia "23:00:00"
 * @returns sformatowany zakres "6:00 - 23:00"
 */
export const formatTimeRange = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) return "Zamknięte";

  const formattedStart = formatTime(startTime);
  const formattedEnd = formatTime(endTime);

  if (!formattedStart || !formattedEnd) return "Zamknięte";

  return `${formattedStart} - ${formattedEnd}`;
};

/**
 * Grupuje godziny otwarcia w czytelny format
 * @param openHours - tablica godzin [Pon, Wt, Śr, Czw, Pt, Sob, Niedz]
 * @returns sformatowany string z pogrupowanymi godzinami
 */
export const formatGroupedHours = (openHours: string[][]): string => {
  if (!openHours || openHours.length !== 7) {
    return "Brak informacji o godzinach";
  }

  const formatHour = (dayHours: string[] | null): string | null => {
    if (!dayHours || dayHours.length !== 2) return null;
    return formatTimeRange(dayHours[0] || "", dayHours[1] || "");
  };

  // Formatuj godziny dla każdego dnia
  const mondayToFriday = openHours.slice(0, 5); // Pon-Pt
  const saturday = openHours[5]; // Sobota
  const sunday = openHours[6]; // Niedziela

  const result: string[] = [];

  // Sprawdź czy Pon-Pt mają te same godziny
  const firstWeekdayHours = formatHour(mondayToFriday[0] || null);
  const allWeekdaysSame = mondayToFriday.every((day) => {
    const dayFormatted = formatHour(day);
    return dayFormatted === firstWeekdayHours;
  });

  if (allWeekdaysSame && firstWeekdayHours) {
    result.push(`Pon-Pt: ${firstWeekdayHours}`);
  } else {
    // Jeśli dni robocze mają różne godziny, wyświetl osobno
    const dayNames = ["Pon", "Wt", "Śr", "Czw", "Pt"];
    mondayToFriday.forEach((day, index) => {
      const formatted = formatHour(day);
      if (formatted) {
        result.push(`${dayNames[index]}: ${formatted}`);
      } else {
        result.push(`${dayNames[index]}: zamknięte`);
      }
    });
  }

  // Sobota
  const saturdayFormatted = formatHour(saturday || null);
  if (saturdayFormatted) {
    result.push(`Sob: ${saturdayFormatted}`);
  } else {
    result.push("Sob: zamknięte");
  }

  // Niedziela
  const sundayFormatted = formatHour(sunday || null);
  if (sundayFormatted) {
    result.push(`Niedz: ${sundayFormatted}`);
  } else {
    result.push("Niedz: zamknięte");
  }

  return result.join(", ");
};

/**
 * Pobiera godziny dla dzisiejszego dnia
 * @param openHours - tablica godzin
 * @returns godziny dla dzisiejszego dnia
 */
export const getTodayHours = (openHours: string[][]): string => {
  if (!openHours || !Array.isArray(openHours)) return "Zamknięte";

  const today = new Date().getDay(); // 0 = niedziela, 1 = poniedziałek, etc.
  const dayIndex = today === 0 ? 6 : today - 1; // Konwersja na format gdzie 0 = poniedziałek

  if (
    openHours[dayIndex] &&
    Array.isArray(openHours[dayIndex]) &&
    openHours[dayIndex].length >= 2
  ) {
    const [start, end] = openHours[dayIndex];
    if (start && end) {
      return formatTimeRange(start, end);
    }
  }

  return "Zamknięte";
};

/**
 * Sprawdza czy punkt jest otwarty teraz
 * @param openHours - tablica godzin
 * @returns true jeśli punkt jest otwarty
 */
export const isOpenNow = (openHours: string[][]): boolean => {
  if (!openHours || !Array.isArray(openHours)) return false;

  const now = new Date();
  const today = now.getDay();
  const dayIndex = today === 0 ? 6 : today - 1;

  if (
    !openHours[dayIndex] ||
    !Array.isArray(openHours[dayIndex]) ||
    openHours[dayIndex].length < 2
  ) {
    return false;
  }

  const [startTime, endTime] = openHours[dayIndex];
  if (!startTime || !endTime) return false;

  const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:00`;

  return currentTime >= startTime && currentTime <= endTime;
};

/**
 * Skrócona wersja godzin - tylko najważniejsze informacje
 * @param openHours - tablica godzin
 * @returns skrócony format godzin
 */
export const formatCompactHours = (openHours: string[][]): string => {
  if (!openHours || !Array.isArray(openHours) || openHours.length < 7) {
    return "Brak informacji o godzinach";
  }

  const mondayToFriday = openHours.slice(0, 5);
  const weekend = openHours.slice(5, 7);

  // Sprawdź czy dni robocze mają te same godziny
  const firstDay = mondayToFriday[0];
  if (!firstDay || firstDay.length < 2) {
    return "Brak informacji o godzinach";
  }

  const firstWeekdayHours = formatTimeRange(
    firstDay[0] || "",
    firstDay[1] || "",
  );

  const allWeekdaysSame = mondayToFriday.every((day) => {
    if (!day || day.length < 2) return false;
    return formatTimeRange(day[0] || "", day[1] || "") === firstWeekdayHours;
  });

  if (allWeekdaysSame) {
    const weekendHours = weekend.map((day) => {
      if (!day || day.length < 2) return "Zamknięte";
      return formatTimeRange(day[0] || "", day[1] || "");
    });

    if (weekendHours[0] === weekendHours[1]) {
      // Weekend ma te same godziny
      return `Pon-Pt: ${firstWeekdayHours}, weekend: ${weekendHours[0]}`;
    } else {
      // Weekend ma różne godziny
      return `Pon-Pt: ${firstWeekdayHours}, Sob: ${weekendHours[0]}, Niedz: ${weekendHours[1]}`;
    }
  }

  // Fallback do pełnego formatu
  return formatGroupedHours(openHours);
};
